import { useCallback, useRef, useState } from 'react';
import Moveable, {
  OnDrag,
  OnDragEnd,
  OnDragGroup,
  OnDragGroupEnd,
  OnDragGroupStart,
  OnDragStart,
  OnResize,
  OnResizeEnd,
  OnResizeGroup,
  OnResizeGroupEnd,
  OnResizeGroupStart,
  OnResizeStart,
  OnRotate,
  OnRotateEnd,
  OnRotateGroup,
  OnRotateGroupEnd,
} from 'react-moveable';
import Selecto, { OnSelect } from 'react-selecto';

import {
  OnElementsChangeProps,
  SlideElement,
} from '../components/SlideComponents/slide.types';
import {
  isSlideElementText,
  PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME,
} from '../components/SlideComponents/slideUtil';
import { notUndefined } from '../utils/general';
import {
  DesignerUIStateActions,
  ElementTransformationProperties,
  useDesignerUIContext,
} from './useDesignerUIState';

const useMoveableCallbacks = ({
  elementsToRender,
  aspect,
  onElementsChange,
  handleElementsDelete,
  handleElementInteraction,
  handleElementSelect,
  selectedElements,
}: {
  elementsToRender: SlideElement[];
  aspect: { width: number; height: number };
  onElementsChange: (props: OnElementsChangeProps) => void;
  handleElementsDelete: (elements: SlideElement[]) => void;
  handleElementInteraction: (element: SlideElement) => void;
  handleElementSelect: (newTargets: Array<HTMLElement | SVGElement>) => void;
  selectedElements: SlideElement[] | null;
}) => {
  const [isSelecting, setIsSelecting] = useState(false);

  const moveableRef = useRef<Moveable>(null);
  const selectoRef = useRef<Selecto>(null);
  const [resizeDirection, setResizeDirection] = useState<number[] | undefined>(undefined);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const elementTransformPropperies = useDesignerUIContext(
    c => c.state.elementTransformationProperties,
  );
  const isDrawerOpen = useDesignerUIContext(context => context.state.isDrawerOpen);
  const onCloseDrawer = useDesignerUIContext(context => context.handleCloseDrawer);
  const selectedTargetElements = useDesignerUIContext(
    context => context.state.selectedTargetElements,
  );

  const isElementOutsideOfBox = useCallback(
    (x: number, y: number, width: number, height: number, angle: number): boolean => {
      //TODO make it work with multiple elements

      const areaBounds = {
        minX: 0,
        minY: 0,
        maxX: aspect.width,
        maxY: aspect.height,
      };

      const topLeft = { x, y: y };
      const topRight = { x: x + width, y: y };
      const bottomLeft = { x, y: y + height };
      const bottomRight = { x: x + width, y: y + height };

      function rotatePoint(point: { x: number; y: number }, angle: number) {
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const radians = (angle * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const newX = centerX + (point.x - centerX) * cos - (point.y - centerY) * sin;
        const newY = centerY + (point.x - centerX) * sin + (point.y - centerY) * cos;
        return { x: newX, y: newY };
      }

      const rotatedTopLeft = rotatePoint(topLeft, angle);
      const rotatedTopRight = rotatePoint(topRight, angle);
      const rotatedBottomRight = rotatePoint(bottomRight, angle);
      const rotatedBottomLeft = rotatePoint(bottomLeft, angle);

      const isFullyAboveTheSlide =
        rotatedTopLeft.y < areaBounds.minY &&
        rotatedTopRight.y < areaBounds.minY &&
        rotatedBottomRight.y < areaBounds.minY &&
        rotatedBottomLeft.y < areaBounds.minY;
      const isFullyBelowTheSlide =
        rotatedTopLeft.y > areaBounds.maxY &&
        rotatedTopRight.y > areaBounds.maxY &&
        rotatedBottomRight.y > areaBounds.maxY &&
        rotatedBottomLeft.y > areaBounds.maxY;
      const isToTheLeftOfTheSlide =
        rotatedTopLeft.x < areaBounds.minX &&
        rotatedTopRight.x < areaBounds.minX &&
        rotatedBottomRight.x < areaBounds.minX &&
        rotatedBottomLeft.x < areaBounds.minX;
      const isToTheRightOfTheSlide =
        rotatedTopLeft.x > areaBounds.maxX &&
        rotatedTopRight.x > areaBounds.maxX &&
        rotatedBottomRight.x > areaBounds.maxX &&
        rotatedBottomLeft.x > areaBounds.maxX;

      // Check if all four corners are outside the area boundaries
      const isOutside =
        isFullyAboveTheSlide ||
        isFullyBelowTheSlide ||
        isToTheLeftOfTheSlide ||
        isToTheRightOfTheSlide;

      return isOutside;
    },
    [aspect],
  );

  const onMoveableDragStart = useCallback(
    (e: OnDragStart) => {
      if (e.target.className.includes('locked')) {
        const selectedElement = elementsToRender.find(
          element => element.id === e.target.id,
        );
        if (!selectedElement) return;
        handleElementInteraction(selectedElement);
        e.stopDrag();
      }
    },
    [elementsToRender, handleElementInteraction],
  );
  const onMoveableDrag = useCallback(
    ({ target, dist, stopDrag }: OnDrag) => {
      // only works when single element is selected
      const selectedElement = elementsToRender.find(e => e.id === target.id);

      if (!selectedElement) {
        return;
      }
      if (selectedElement.lockedPosition) {
        stopDrag();
        return;
      }
      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: [
          {
            transformX: selectedElement.x + dist[0],
            transformY: selectedElement.y + dist[1],
            transformAngle: selectedElement.angle,
            transformHeight: selectedElement.height,
            transformWidth: selectedElement.width,
            id: selectedElement.id,
          },
        ],
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );
  const onMoveableDragEnd = useCallback(
    ({ lastEvent, target }: OnDragEnd) => {
      // only works when single element is selected
      if (!target) return;
      if (!lastEvent) return;

      const selectedElement = elementsToRender.find(e => e.id === target.id);
      if (!selectedElement) return;
      const newElement = { ...selectedElement };

      newElement.x = selectedElement.x + lastEvent.beforeDist[0];
      newElement.y = selectedElement.y + lastEvent.beforeDist[1];

      onElementsChange({
        updatedElements: [newElement],
      });

      dispatch({ type: DesignerUIStateActions.stopTransformingElement });

      if (
        isElementOutsideOfBox(
          newElement.x,
          newElement.y,
          newElement.width,
          newElement.height,
          newElement.angle,
        )
      ) {
        handleElementsDelete([newElement]);
      }
    },
    [
      dispatch,
      elementsToRender,
      handleElementsDelete,
      isElementOutsideOfBox,
      onElementsChange,
    ],
  );
  const onMoveableDragGroupStart = useCallback((e: OnDragGroupStart) => {
    if (e.targets.some(e => e.className.includes('locked'))) {
      e.stopDrag();
    }
  }, []);

  const onMoveableDragGroup = useCallback(
    (e: OnDragGroup) => {
      const targets = e.targets;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);
      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));
      // if one is locked we dont want to move any of them
      if (relevantElements.some(e => e.lockedPosition)) {
        e.stopDrag();
        return;
      }

      const distY = e.dist[1];
      const distX = e.dist[0];

      const newTransformProps: ElementTransformationProperties[] = relevantElements.map(
        element => {
          return {
            id: element.id,
            transformX: element.x + distX,
            transformY: element.y + distY,
            transformAngle: element.angle,
            transformHeight: element.height,
            transformWidth: element.width,
          };
        },
      );
      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: newTransformProps,
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );

  const onMoveableDragGroupEnd = useCallback(
    (e: OnDragGroupEnd) => {
      const targets = e.targets;
      const lastEvent = e.lastEvent;
      if (!lastEvent) return;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);
      const distX = e.lastEvent.beforeDist[0];
      const distY = e.lastEvent.beforeDist[1];
      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));
      const updatedElements = relevantElements.map(e => {
        return {
          ...e,
          x: e.x + distX,
          y: e.y + distY,
        };
      });

      // if You drag the elements outside of the slide, delete them
      const shouldNotDelete = updatedElements.some(e => {
        return !isElementOutsideOfBox(e.x, e.y, e.width, e.height, e.angle);
      });
      if (shouldNotDelete === false) {
        handleElementsDelete(updatedElements);
        return;
      }

      onElementsChange({ updatedElements });

      dispatch({ type: DesignerUIStateActions.stopTransformingElement });
    },
    [
      dispatch,
      elementsToRender,
      handleElementsDelete,
      isElementOutsideOfBox,
      onElementsChange,
    ],
  );
  const onMoveableResizeStart = useCallback(({ direction }: OnResizeStart) => {
    setResizeDirection(direction);
  }, []);

  const onMoveableResize = useCallback(
    (e: OnResize) => {
      e.target.style.width = `${e.width}px`;

      e.target.style.transform = `translate(${e.drag.dist[0]}px, ${e.drag.dist[1]}px)`;
      let transformScale;

      const target = e.target;
      const selectedElement = elementsToRender?.find(e => e.id === target.id);
      if (!selectedElement) return;
      if (selectedElement.lockedPosition) {
        e.stopDrag();
        return;
      }

      const isResizingByCorner = e.direction[0] !== 0 && e.direction[1] !== 0;
      // we resize SlideTextElements by changing the height of the slate editor
      const shouldResizeHeight =
        isSlideElementText(selectedElement) && isResizingByCorner;
      if (shouldResizeHeight) {
        const elementScale = selectedElement.scale;
        const elementHeightScale = elementScale || 1;
        const referenceHeight = selectedElement.height / elementHeightScale;

        const newHeight = e.height;

        const newScaleHeight = newHeight / referenceHeight;

        transformScale = newScaleHeight;
      }

      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: [
          {
            transformAngle: selectedElement.angle,
            transformWidth: e.width,
            transformHeight: e.height,
            transformX: selectedElement.x + e.drag.dist[0],
            transformY: selectedElement.y + e.drag.dist[1],
            transformScale: transformScale,
            id: selectedElement.id,
          },
        ],
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );

  const onMoveableResizeEnd = useCallback(
    ({ lastEvent, target }: OnResizeEnd) => {
      setResizeDirection(undefined);
      if (!lastEvent) return;
      if (!target) return;

      const selectedElement = elementsToRender.find(e => e.id === target.id);
      if (!selectedElement) return;

      const newElement: SlideElement = { ...selectedElement };
      let newHeight = lastEvent.height;

      // since text is dynamic in height we must check the child and update
      // both the moveable box and the element with the slate height
      const height = target.style.height;
      if (isSlideElementText(selectedElement)) {
        const richTextElement = document.getElementById(
          `slide-element-core-viwer${selectedElement.id}`,
        );

        const transformScale =
          elementTransformPropperies?.[0].transformScale || selectedElement?.scale || 1;
        if (richTextElement) {
          //@ts-ignore
          const clientHeight = richTextElement.clientHeight * transformScale;

          if (height !== `${clientHeight}px`) {
            target.style.height = `${clientHeight}px`;
            newHeight = clientHeight;
          }
        }
        if (transformScale) {
          newElement.scale = transformScale;
        }
      }
      newElement.width = lastEvent.width;
      newElement.height = newHeight;
      newElement.x = selectedElement.x + lastEvent.drag.dist[0];
      newElement.y = selectedElement.y + lastEvent.drag.dist[1];
      onElementsChange({
        updatedElements: [newElement],
      });
      dispatch({ type: DesignerUIStateActions.stopTransformingElement });
    },
    [dispatch, elementTransformPropperies, elementsToRender, onElementsChange],
  );
  const onMoveableResizeGroupStart = useCallback((e: OnResizeGroupStart) => {
    setResizeDirection(e.direction);
  }, []);
  const onMoveableResizeGroup = useCallback(
    (e: OnResizeGroup) => {
      const targets = e.targets;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);

      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));
      if (relevantElements.some(e => e.lockedPosition)) {
        e.stopDrag();
        return;
      }
      const newTransformProps: ElementTransformationProperties[] = relevantElements
        .map(element => {
          const event = e.events.find(ev => ev.target.id === element.id);
          let transformScale: number | undefined;
          if (!event) return undefined;
          // we resize SlideTextElements by changing the scale of the element
          const shouldResizeHeight =
            isSlideElementText(element) && Math.abs(event.direction[1]) > 0;

          // calculate a new element scale
          if (shouldResizeHeight) {
            const elementScale = element.scale;

            const elementHeightScale = elementScale || 1;
            const referenceHeight = element.height / elementHeightScale;

            const newHeight = event.height;

            const newScaleHeight = newHeight / referenceHeight;

            transformScale = newScaleHeight;
          }

          return {
            id: element.id,
            transformX: element.x + event.drag.dist[0],
            transformY: element.y + event.drag.dist[1],
            transformAngle: element.angle,
            transformHeight: event.height,
            transformWidth: event.width,
            transformScale,
          };
        })
        .filter(notUndefined);

      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: newTransformProps,
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );
  const onMoveableResizeGroupEnd = useCallback(
    (e: OnResizeGroupEnd) => {
      const targets = e.targets;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);

      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));

      const updatedElements = relevantElements
        .map(element => {
          const event = e.events.find(ev => ev.target.id === element.id);
          if (!event) return undefined;
          if (!event.lastEvent) return undefined;
          const newY = event.lastEvent.drag.beforeDist[1];
          const elementTransformElement = elementTransformPropperies?.find(
            e => e.id === element.id,
          );
          const newScale = elementTransformElement?.transformScale || element?.scale || 1;

          const newHeight = event.lastEvent.height;

          return {
            ...element,
            x: element.x + event.lastEvent.drag.beforeDist[0],
            y: element.y + newY,
            height: newHeight,
            width: event.lastEvent.width,
            scale: newScale,
          };
        })
        .filter(notUndefined);

      onElementsChange({ updatedElements });

      dispatch({ type: DesignerUIStateActions.stopTransformingElement });
      setResizeDirection(undefined);
    },
    [dispatch, elementTransformPropperies, elementsToRender, onElementsChange],
  );

  const onMoveableRotate = useCallback(
    (e: OnRotate) => {
      const target = e.target;
      const selectedElement = elementsToRender.find(e => e.id === target.id);
      if (!selectedElement) return;
      const dist = e.dist;
      const newAngle =
        selectedElement.angle + dist >= 360
          ? selectedElement.angle + dist - 360
          : selectedElement.angle + dist < 0
          ? selectedElement.angle + dist + 360
          : selectedElement.angle + dist;

      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: [
          {
            transformWidth: selectedElement.width,
            transformHeight: selectedElement.height,
            transformAngle: newAngle,
            transformX: selectedElement.x + e.drag.dist[0],
            transformY: selectedElement.y + e.drag.dist[1],
            id: selectedElement.id,
          },
        ],
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );

  const onMoveableRotateEnd = useCallback(
    ({ lastEvent, target }: OnRotateEnd) => {
      if (!lastEvent || !target) return;
      const selectedElement = elementsToRender.find(e => e.id === target.id);
      if (!selectedElement) return;
      const newElement = { ...selectedElement };
      const newAngle =
        selectedElement.angle + lastEvent.dist >= 360
          ? selectedElement.angle + lastEvent.dist - 360
          : selectedElement.angle + lastEvent.dist < 0
          ? selectedElement.angle + lastEvent.dist + 360
          : selectedElement.angle + lastEvent.dist;
      newElement.angle = newAngle;

      onElementsChange({
        updatedElements: [newElement],
      });
      dispatch({ type: DesignerUIStateActions.stopTransformingElement });
    },
    [dispatch, elementsToRender, onElementsChange],
  );

  const onMoveableRotateGroup = useCallback(
    (e: OnRotateGroup) => {
      const targets = e.targets;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);
      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));
      const newTransformProps: ElementTransformationProperties[] = relevantElements
        .map(element => {
          const event = e.events.find(ev => ev.target.id === element.id);
          if (!event) return undefined;
          const newAngle =
            element.angle + event.dist >= 360
              ? element.angle + event.dist - 360
              : element.angle + event.dist < 0
              ? element.angle + event.dist + 360
              : element.angle + event.dist;
          return {
            id: element.id,
            transformX: element.x + event.drag.dist[0],
            transformY: element.y + event.drag.dist[1],
            transformAngle: newAngle,
            transformHeight: element.height,
            transformWidth: element.width,
          };
        })
        .filter(notUndefined);
      dispatch({
        type: DesignerUIStateActions.transformElements,
        elementTransformationProperties: newTransformProps,
        shouldSetTransformingElement: true,
      });
    },
    [dispatch, elementsToRender],
  );

  const onMoveableRotateGroupEnd = useCallback(
    (e: OnRotateGroupEnd) => {
      const targets = e.targets;
      if (!targets) return;
      const targetIds = targets.map(t => t.id);
      const relevantElements = elementsToRender.filter(e => targetIds.includes(e.id));
      const updatedElements = relevantElements
        .map(element => {
          const event = e.events.find(ev => ev.target.id === element.id);
          if (!event) return undefined;
          if (!event.lastEvent) return undefined;
          const newAngle =
            element.angle + event.lastEvent.dist >= 360
              ? element.angle + event.lastEvent.dist - 360
              : element.angle + event.lastEvent.dist < 0
              ? element.angle + event.lastEvent.dist + 360
              : element.angle + event.lastEvent.dist;
          return {
            ...element,
            x: element.x + event.lastEvent.drag.beforeDist[0],
            y: element.y + event.lastEvent.drag.beforeDist[1],
            angle: newAngle,
          };
        })
        .filter(notUndefined);
      onElementsChange({ updatedElements });
      dispatch({ type: DesignerUIStateActions.stopTransformingElement });
    },
    [dispatch, elementsToRender, onElementsChange],
  );
  const onSelectStart = useCallback(
    (e: OnSelect) => {
      if (isDrawerOpen) {
        onCloseDrawer();
      }
      setIsSelecting(true);
      const targets = e.selected;
      const srcElement = e.inputEvent.srcElement;
      const classList = JSON.stringify(srcElement?.classList);
      if (
        classList &&
        (classList.includes(PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME) ||
        classList.includes('epr-btn') || // Emoji menu buttons
        classList.includes('epr-emoji') || // Emoju menu item
          classList.includes('ant-slider-handle'))
      ) {
        e.inputEvent.preventDefault();
        return;
      }

      if (targets.length === 0) {
        handleElementSelect([]);
        return;
      }
      if (e.isDragStartEnd && e.selected.length === 1 && e.added.length === 1) {
        moveableRef.current!.waitToChangeTarget().then(() => {
          e.inputEvent.preventDefault();

          // this is a hack fix to prevent dragging when adding new elements
          if (selectedTargetElements?.[0] === `.target${e.inputEvent.srcElement.id}`)
            return;
          if (selectedElements?.some(e => e.lockedPosition)) return;
          moveableRef.current!.dragStart(e.inputEvent);
        });
      }
    },
    [handleElementSelect, selectedElements, selectedTargetElements, isDrawerOpen],
  );

  const onSelect = useCallback(
    (e: OnSelect) => {
      const srcElement = e.inputEvent.srcElement;
      const classList = JSON.stringify(srcElement?.classList);

      if (
        classList &&
        (classList.includes(PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME) ||
        classList.includes('epr-btn') || // Emoji menu buttons
        classList.includes('epr-emoji') || // Emoju menu item
          classList.includes('ant-slider-handle'))
      ) {
        return;
      }
      if (e.selected.length >= 1) {
        // if you want to select many elements at once, you can only select the ones that are not locked
        const filteredLockedPositions = e.selected.filter(e => {
          const element = elementsToRender.find(el => el.id === e.id);
          return !element?.lockedPosition;
        });
        if (
          filteredLockedPositions.length > 0 &&
          (e.inputEvent.type === 'mousemove' || e.inputEvent.type === 'touchmove')
        ) {
          handleElementSelect(filteredLockedPositions);
          return;
        }
      }
      // since none is selected and you click drag an element we want to start dragging it

      handleElementSelect(e.selected);
    },
    [elementsToRender, handleElementSelect],
  );
  const onSelectEnd = useCallback(() => {
    setIsSelecting(false);
  }, []);

  const onSelectDragStart = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const target = e.inputEvent.target;
      const srcElement = e.inputEvent.srcElement;
      const classList = JSON.stringify(srcElement?.classList);
      if (
        moveableRef.current!.isMoveableElement(target) ||
        selectedTargetElements!.includes(target.id) ||
        classList.includes(PREVENT_UNSELECT_SLIDE_ELEMENT_CLASSNAME) ||
        classList.includes('ant-slider-handle')
      ) {
        e.stop();
        e.preventDefault();
        srcElement.focus();
      }
    },
    [selectedTargetElements],
  );

  return {
    selectoRef,
    moveableRef,
    isSelecting,
    resizeDirection,
    isResizing: resizeDirection !== undefined,
    onMoveableDragStart,
    onMoveableDrag,
    onMoveableDragEnd,
    onMoveableDragGroupStart,
    onMoveableDragGroup,
    onMoveableDragGroupEnd,
    onMoveableResizeStart,
    onMoveableResize,
    onMoveableResizeEnd,
    onMoveableResizeGroupStart,
    onMoveableResizeGroup,
    onMoveableResizeGroupEnd,
    onMoveableRotate,
    onMoveableRotateEnd,
    onMoveableRotateGroup,
    onMoveableRotateGroupEnd,
    onSelectStart,
    onSelect,
    onSelectEnd,
    onSelectDragStart,
  };
};

export default useMoveableCallbacks;
